import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "./api";
import { authFetchJson } from "./auth-fetch";
import { useAuthStore } from "./auth";
import type { components } from "@/api/booqly-api";

type TenantDto = components["schemas"]["TenantDto"];

/**
 * Calling-tenant + calling-user round-trip used by the onboarding gate
 * (`onboardingCompletedAt == null` → wizard) and Settings pages.
 */

export type TenantMe = components["schemas"]["TenantMeResponse"];
export type TenantUpdate = components["schemas"]["UpdateTenantRequest"];

export function useTenantMe() {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["tenant", "me"],
    enabled: !!accessToken,
    // No refetch on focus — tenant config changes infrequently and we
    // care about the response shape staying stable across the wizard.
    refetchOnWindowFocus: false,
    queryFn: async () => unwrap(await api.GET("/api/v1/tenants/me", {})),
  });
}

export function useUpdateTenantMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: TenantUpdate) =>
      unwrap(await api.PATCH("/api/v1/tenants/me", { body })),
    onSuccess: (tenant) => {
      // Sync cache write with the returned DTO so any consumer reading
      // useTenantMe() right after this resolves sees the fresh tenant
      // — including the onboarding gate. Skips a redundant refetch.
      qc.setQueryData<TenantMe>(["tenant", "me"], (old) =>
        old ? { ...old, tenant } : old,
      );
    },
  });
}

export function useCompleteOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST("/api/v1/tenants/me/onboarding/complete", {}),
      ),
    onSuccess: (tenant) => {
      qc.setQueryData<TenantMe>(["tenant", "me"], (old) =>
        old ? { ...old, tenant } : old,
      );
    },
  });
}

/**
 * Multipart upload of the tenant's workspace logo. Server stores the
 * MinIO key on `tenants.logo_url` and returns the refreshed `TenantDto`
 * with a presigned `logoUrl`. Cache is updated synchronously — same
 * race-free pattern as the JSON mutations above.
 */
export function useUploadTenantLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      return authFetchJson<TenantDto>("/api/v1/tenants/me/logo", {
        method: "POST",
        body: fd,
      });
    },
    onSuccess: (tenant) => {
      qc.setQueryData<TenantMe>(["tenant", "me"], (old) =>
        old ? { ...old, tenant } : old,
      );
    },
  });
}
